import {
	AUTH_TOKENS_UPDATED,
	AUTH_REFRESH_REQUEST,
	AUTH_REFRESH_RESPONSE,
	AUTH_REFRESH_FAILED,
	AUTH_LOGOUT_REQUEST,
} from './parentAuthMessaging';

export {
	AUTH_TOKENS_UPDATED,
	AUTH_REFRESH_REQUEST,
	AUTH_REFRESH_RESPONSE,
	AUTH_REFRESH_FAILED,
	AUTH_LOGOUT_REQUEST,
};

const REFRESH_TIMEOUT_MS = 15000;

let refreshRequestId = 0;
const pendingRefreshRequests = new Map();
let parentRefreshInFlight = null;

const parseMessage = (raw) => {
	if (!raw) return null;
	const data = typeof raw === 'string'
		? (() => {
				try {
					return JSON.parse(raw);
				} catch (e) {
					return null;
				}
		  })()
		: raw;
	return data?.type ? data : null;
};

const redirectToMainAppLogin = () => {
	const {
		mainApp: { baseUrl, loginPagePath },
	} = window['runConfig'];
	window.location.href = `${baseUrl}${loginPagePath}?redirectUri=${encodeURIComponent(window.location.href)}`;
};

export const requestParentTokenRefresh = () => {
	if (parentRefreshInFlight) {
		return parentRefreshInFlight;
	}

	parentRefreshInFlight = new Promise((resolve, reject) => {
		if (window.parent === window) {
			reject(new Error('Not embedded in parent app'));
			return;
		}

		const requestId = ++refreshRequestId;
		const timer = setTimeout(() => {
			pendingRefreshRequests.delete(requestId);
			reject(new Error('Parent token refresh timed out'));
		}, REFRESH_TIMEOUT_MS);

		pendingRefreshRequests.set(requestId, {
			resolve: (accessToken) => {
				clearTimeout(timer);
				resolve({ accessToken });
			},
			reject: (error) => {
				clearTimeout(timer);
				reject(error);
			},
		});

		window.parent.postMessage(
			JSON.stringify({ type: AUTH_REFRESH_REQUEST, request_id: requestId }),
			'*'
		);
	}).finally(() => {
		parentRefreshInFlight = null;
	});

	return parentRefreshInFlight;
};

export const requestParentLogout = () => {
	if (window.parent === window) {
		redirectToMainAppLogin();
		return;
	}

	window.parent.postMessage(
		JSON.stringify({ type: AUTH_LOGOUT_REQUEST }),
		'*'
	);
};

export const initParentAuthListener = ({ onAccessTokenUpdated } = {}) => {
	window.addEventListener('message', (event) => {
		const data = parseMessage(event.data);
		if (!data) return;

		if (data.type === AUTH_TOKENS_UPDATED) {
			if (data.access_token) {
				onAccessTokenUpdated?.(data.access_token);
			}
			return;
		}

		if (data.type === AUTH_REFRESH_RESPONSE && data.request_id != null) {
			const pending = pendingRefreshRequests.get(data.request_id);
			if (!pending) return;

			pendingRefreshRequests.delete(data.request_id);
			if (data.access_token) {
				onAccessTokenUpdated?.(data.access_token);
			}
			pending.resolve(data.access_token);
			return;
		}

		if (data.type === AUTH_REFRESH_FAILED && data.request_id != null) {
			const pending = pendingRefreshRequests.get(data.request_id);
			if (!pending) return;

			pendingRefreshRequests.delete(data.request_id);
			pending.reject(new Error('Parent token refresh failed'));
			redirectToMainAppLogin();
		}
	});
};
